import { mm } from './units.js';
import type { Axis, CabinetResult, HardwareItem, Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, PanelOrientation } from './document.js';
import { computeEdgeCoverage, makePanel, partBox } from './geometry.js';
import { detectCollisions, MAX_ENCLOSED_GAP } from './cabinet.js';
import { TOLERANCE } from './units.js';
import { buildShelfSet, MAX_SHELVES } from './shelves.js';
import { buildFront } from './fronts.js';

export const MAX_REPEAT = 50;

const AXES: Record<PanelOrientation, { lengthAxis: Axis; widthAxis: Axis; thicknessAxis: Axis }> = {
  horizontal: { lengthAxis: 'x', widthAxis: 'z', thicknessAxis: 'y' },
  'vertical-side': { lengthAxis: 'y', widthAxis: 'z', thicknessAxis: 'x' },
  'vertical-front': { lengthAxis: 'x', widthAxis: 'y', thicknessAxis: 'z' },
};

const IDX: Record<Axis, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };

export function panelSize(p: Pick<AssemblyPanel, 'orientation' | 'length' | 'width' | 'thickness'>): Vec3 {
  const a = AXES[p.orientation];
  const size: Vec3 = [0, 0, 0];
  size[IDX[a.lengthAxis]] = p.length;
  size[IDX[a.widthAxis]] = p.width;
  size[IDX[a.thicknessAxis]] = p.thickness;
  return size;
}

function validatePanel(inst: AssemblyInstance, p: AssemblyPanel): Issue[] {
  const issues: Issue[] = [];
  const err = (message: string) => issues.push({ severity: 'error', code: 'panel.invalid', message: `${p.label}: ${message}`, cabinetId: inst.id, partId: `${inst.id}/${p.key}` });
  for (const [k, v] of [
    ['dužina', p.length],
    ['širina', p.width],
    ['debljina', p.thickness],
  ] as const) {
    if (!Number.isInteger(v) || v <= 0) err(`${k} mora biti pozitivna.`);
    else if (v > mm(5000)) err(`${k} prelazi 5000 mm.`);
  }
  if (p.thickness > mm(100)) err('debljina prelazi 100 mm.');
  if (p.repeat) {
    if (!Number.isInteger(p.repeat.count) || p.repeat.count < 1 || p.repeat.count > MAX_REPEAT) err(`broj ponavljanja mora biti 1–${MAX_REPEAT}.`);
    if (!Number.isInteger(p.repeat.spacing) || p.repeat.spacing <= 0) err('razmak ponavljanja mora biti pozitivan.');
  }
  return issues;
}

/** Expands an assembly's panels (including repeats) into manufacturing parts in assembly-local space. */
export function buildAssembly(inst: AssemblyInstance): CabinetResult {
  const issues: Issue[] = [];
  const keys = new Set<string>();
  for (const p of inst.panels) {
    if (!/^[A-Za-z0-9_-]{1,60}$/.test(p.key) || keys.has(p.key)) {
      issues.push({ severity: 'error', code: 'panel.key', message: `Neispravan ili dupli ključ ploče "${p.key}".`, cabinetId: inst.id });
    }
    keys.add(p.key);
    issues.push(...validatePanel(inst, p));
  }
  for (const s of inst.shelfSets ?? []) {
    const err = (message: string) => issues.push({ severity: 'error', code: 'shelves.invalid', message: `${s.label}: ${message}`, cabinetId: inst.id });
    if (!/^[A-Za-z0-9_-]{1,60}$/.test(s.id) || keys.has(s.id)) err(`neispravan ili dupli ključ "${s.id}".`);
    keys.add(s.id);
    if (!Number.isInteger(s.count) || s.count < 1 || s.count > MAX_SHELVES) err(`broj polica mora biti 1–${MAX_SHELVES}.`);
    if (!Number.isInteger(s.thickness) || s.thickness <= 0 || s.thickness > mm(100)) err('debljina mora biti 0–100 mm.');
    if (!Number.isInteger(s.gap) || s.gap < 0 || s.gap > mm(100)) err('zazor mora biti 0–100 mm.');
  }
  if (inst.frontReveal !== undefined && (!Number.isInteger(inst.frontReveal) || inst.frontReveal < 0 || inst.frontReveal > mm(20)))
    issues.push({ severity: 'error', code: 'fronts.invalid', message: 'Fuga fronti mora biti 0–20 mm.', cabinetId: inst.id });
  for (const f of inst.fronts ?? []) {
    const err = (message: string) => issues.push({ severity: 'error', code: 'fronts.invalid', message: `${f.label}: ${message}`, cabinetId: inst.id });
    if (!/^[A-Za-z0-9_-]{1,60}$/.test(f.id) || keys.has(f.id)) err(`neispravan ili dupli ključ "${f.id}".`);
    keys.add(f.id);
    if (!f.spaces.length) err('fronta nema nijedan prostor.');
    if (!Number.isInteger(f.thickness) || f.thickness <= 0 || f.thickness > mm(100)) err('debljina mora biti 0–100 mm.');
    if (f.clearance !== undefined && (!Number.isInteger(f.clearance) || f.clearance < 0 || f.clearance > mm(20))) err('zazor mora biti 0–20 mm.');
  }
  if (issues.some((i) => i.severity === 'error')) return { cabinetId: inst.id, parts: [], hardware: [], issues, bounds: { size: [0, 0, 0] } };

  const parts: PanelPart[] = [];
  for (const p of inst.panels) {
    const a = AXES[p.orientation];
    const size = panelSize(p);
    const count = p.repeat?.count ?? 1;
    for (let i = 0; i < count; i++) {
      const min: Vec3 = [...p.position];
      if (p.repeat) min[IDX[p.repeat.axis]] += i * p.repeat.spacing;
      parts.push(
        makePanel({
          cabinetId: inst.id,
          localKey: count > 1 ? `${p.key}-${i + 1}` : p.key,
          role: 'custom',
          label: count > 1 ? `${p.label} ${i + 1}` : p.label,
          box: { min, size },
          lengthAxis: a.lengthAxis,
          widthAxis: a.widthAxis,
          materialSlot: p.materialSlot,
          grain: p.grain,
        }),
      );
    }
  }
  for (const set of inst.shelfSets ?? []) {
    const r = buildShelfSet(inst, set);
    parts.push(...r.parts);
    issues.push(...r.issues);
  }
  const hardware: HardwareItem[] = [];
  for (const f of inst.fronts ?? []) {
    const r = buildFront(inst, f);
    parts.push(...r.parts);
    hardware.push(...r.hardware);
    issues.push(...r.issues);
  }
  // Declared joints (by panel key, applied to every repeated copy) are intentional overlaps.
  const keyOf = (p: PanelPart) => inst.panels.find((x) => p.localKey === x.key || p.localKey.startsWith(`${x.key}-`))?.key;
  for (const p of inst.panels) {
    for (const j of p.joints) {
      for (const a of parts.filter((x) => keyOf(x) === p.key)) {
        for (const b of parts.filter((x) => keyOf(x) === j.key)) {
          a.connections.push({ partId: b.id, kind: j.kind });
          b.connections.push({ partId: a.id, kind: j.kind });
        }
      }
    }
  }
  const byId = new Map(parts.map((p) => [p.id, p]));
  // Fronts sit on the carcass edges but do not close them (those edges are still banded), and the
  // carcass does not close the fronts' edges: coverage is computed for each group on its own.
  const doors = parts.filter((p) => p.role === 'door');
  const coverage = [...computeEdgeCoverage(parts.filter((p) => p.role !== 'door'), MAX_ENCLOSED_GAP), ...computeEdgeCoverage(doors, MAX_ENCLOSED_GAP)];
  for (const cov of coverage) {
    const part = byId.get(cov.partId)!;
    part.edges[cov.edge].contacts = cov.contacts;
    for (const c of cov.contacts) if (c.gap <= TOLERANCE.contact) part.connections.push({ partId: c.partId, kind: 'butt', edge: cov.edge });
  }
  issues.push(...detectCollisions(inst.id, parts));

  // Normalise bounds from the actual panel extents (origin stays the assembly origin).
  const max: Vec3 = [0, 0, 0];
  for (const p of parts) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    if (b.min.some((v) => v < 0)) {
      issues.push({ severity: 'warning', code: 'panel.negative-position', message: `${p.label} je ispod/iza ishodišta sklopa.`, cabinetId: inst.id, partId: p.id });
    }
  }
  return { cabinetId: inst.id, parts, hardware, issues, bounds: { size: max } };
}

/** Next free panel key like `ploca-3`. */
export function nextPanelKey(inst: AssemblyInstance, prefix = 'ploca'): string {
  const used = new Set(inst.panels.map((p) => p.key));
  let n = inst.panels.length + 1;
  while (used.has(`${prefix}-${n}`)) n++;
  return `${prefix}-${n}`;
}

export function defaultPanel(key: string, orientation: PanelOrientation = 'horizontal'): AssemblyPanel {
  return {
    key,
    label: 'Ploča',
    orientation,
    length: mm(600),
    width: mm(400),
    thickness: mm(18),
    position: [0, 0, 0] as Vec3,
    materialSlot: 'carcass',
    grain: 'length',
    repeat: null,
    joints: [],
  };
}


const ORIENT_OF_THICKNESS: Record<Axis, PanelOrientation> = { y: 'horizontal', x: 'vertical-side', z: 'vertical-front' };

/**
 * Rotates a panel 90° about the given axis through its centre ("flip orientation").
 * Length, width and thickness are kept (only which axis they lie on changes), the grain keeps its
 * physical direction, the centre stays put and the pin rotates with the panel.
 */
export function flipPanel(p: AssemblyPanel, about: Axis): AssemblyPanel {
  const a = AXES[p.orientation];
  const k = IDX[about];
  const [u, v] = ([0, 1, 2] as const).filter((i) => i !== k) as [0 | 1 | 2, 0 | 1 | 2];
  const swap = (i: 0 | 1 | 2): 0 | 1 | 2 => (i === u ? v : i === v ? u : i);
  const NAME: Axis[] = ['x', 'y', 'z'];

  const size = panelSize(p);
  const newSize: Vec3 = [0, 0, 0];
  for (const i of [0, 1, 2] as const) newSize[swap(i)] = size[i];

  const thAxis = NAME[swap(IDX[a.thicknessAxis])]!;
  const orientation = ORIENT_OF_THICKNESS[thAxis];
  const na = AXES[orientation];
  const length = newSize[IDX[na.lengthAxis]];
  const width = newSize[IDX[na.widthAxis]];

  // Grain: the physical axis it ran along, rotated, expressed as length/width of the new orientation.
  let grain = p.grain;
  if (p.grain !== 'none') {
    const g = NAME[swap(IDX[p.grain === 'length' ? a.lengthAxis : a.widthAxis])]!;
    grain = g === na.lengthAxis ? 'length' : 'width';
  }

  // Keep the centre: new min = centre − newSize / 2 (µm, rounded).
  const position: Vec3 = [0, 0, 0];
  for (const i of [0, 1, 2] as const) position[i] = Math.round(p.position[i] + size[i] / 2 - newSize[i] / 2);

  // +90° about `about`: (u, v) → (−v, u) in centred coordinates; fractions follow.
  let pin = p.pin;
  if (pin) {
    const np = [...pin] as typeof pin;
    np[u] = (1 - pin[v]) as typeof pin[0];
    np[v] = pin[u];
    pin = np;
  }
  return { ...p, orientation, length, width, position, grain, ...(pin ? { pin } : {}) };
}
